import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const downloadFile = async (url, filename, config = {}) => {
  const res = await axios.get(url, { ...auth(), responseType: "blob", ...config });
  const href = URL.createObjectURL(res.data);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
};

export const ComplianceManager = () => {
  const [security, setSecurity] = useState(null);
  const [draft, setDraft] = useState({ staging_password: "" });
  const [cookieCatalog, setCookieCatalog] = useState([]);
  const [consentLogs, setConsentLogs] = useState([]);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [securityRes, cookiesRes, consentRes, securityLogsRes] = await Promise.all([
        axios.get(`${API}/security-settings`, auth()),
        axios.get(`${API}/cookie-catalog`),
        axios.get(`${API}/consent-logs?limit=20`, auth()),
        axios.get(`${API}/security-logs?limit=20`, auth()),
      ]);
      setSecurity(securityRes.data);
      setDraft({ ...securityRes.data, staging_password: "" });
      setCookieCatalog(cookiesRes.data || []);
      setConsentLogs(consentRes.data || []);
      setSecurityLogs(securityLogsRes.data || []);
    } catch {
      toast.error("Compliance-Daten konnten nicht geladen werden");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        staging_protection_enabled: !!draft.staging_protection_enabled,
        staging_password: draft.staging_password || null,
        hsts_enabled: !!draft.hsts_enabled,
        security_logging_enabled: !!draft.security_logging_enabled,
        backup_concept: draft.backup_concept,
        two_factor_status: draft.two_factor_status,
      };
      const res = await axios.put(`${API}/security-settings`, payload, auth());
      setSecurity(res.data);
      setDraft({ ...res.data, staging_password: "" });
      toast.success("Sicherheits-Einstellungen gespeichert");
      load();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
    setSaving(false);
  };

  if (!security) return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43] mx-auto"></div></div>;

  return (
    <div className="space-y-6" data-testid="compliance-manager">
      <h2 className="text-2xl font-bold text-[#0A2A43] font-barlow">DSGVO & Sicherheit</h2>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <h3 className="font-semibold text-[#0A2A43]">Sicherheits-Einstellungen</h3>
          <label className="flex items-center justify-between gap-4"><span>Staging-Passwortschutz aktiv</span><input type="checkbox" checked={!!draft.staging_protection_enabled} onChange={(e) => setDraft({ ...draft, staging_protection_enabled: e.target.checked })} data-testid="security-staging-toggle" /></label>
          <label className="flex items-center justify-between gap-4"><span>HSTS aktiv</span><input type="checkbox" checked={!!draft.hsts_enabled} onChange={(e) => setDraft({ ...draft, hsts_enabled: e.target.checked })} data-testid="security-hsts-toggle" /></label>
          <label className="flex items-center justify-between gap-4"><span>Sicherheitslogging aktiv</span><input type="checkbox" checked={!!draft.security_logging_enabled} onChange={(e) => setDraft({ ...draft, security_logging_enabled: e.target.checked })} data-testid="security-logging-toggle" /></label>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Neues Staging-Passwort</label>
            <input type="password" value={draft.staging_password} onChange={(e) => setDraft({ ...draft, staging_password: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="Nur ausfüllen, wenn geändert werden soll" data-testid="security-staging-password-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Backup-Konzept</label>
            <textarea value={draft.backup_concept || ""} onChange={(e) => setDraft({ ...draft, backup_concept: e.target.value })} rows={4} className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none" data-testid="security-backup-concept-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">2FA-Status</label>
            <textarea value={draft.two_factor_status || ""} onChange={(e) => setDraft({ ...draft, two_factor_status: e.target.value })} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none" data-testid="security-2fa-status-input" />
          </div>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-[#0A2A43] text-white text-sm hover:bg-[#0A2A43]/90 disabled:opacity-50" data-testid="security-save-button">{saving ? "Speichern..." : "Speichern"}</button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <h3 className="font-semibold text-[#0A2A43]">Cookie-Liste</h3>
          <div className="space-y-3">
            {cookieCatalog.map((item) => (
              <div key={item.name} className="rounded-lg border bg-gray-50 p-4" data-testid={`cookie-catalog-${item.name}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-[#0A2A43]">{item.name}</p>
                  <span className="text-xs px-2 py-1 rounded-full bg-[#0A2A43] text-white">{item.category}</span>
                </div>
                <p className="text-sm text-gray-600 mt-2">{item.purpose}</p>
                <p className="text-xs text-gray-500 mt-2">Anbieter: {item.provider} · Dauer: {item.storage_duration} · Speicherort: {item.storage_location}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <div className="flex items-center justify-between gap-3"><h3 className="font-semibold text-[#0A2A43]">Consent-Logs</h3><div className="flex gap-2"><button onClick={() => downloadFile(`${API}/consent-logs/export?format=csv`, "consent-logs.csv")} className="px-3 py-2 rounded-lg bg-gray-100 text-sm">CSV</button><button onClick={() => downloadFile(`${API}/consent-logs/export?format=json`, "consent-logs.json")} className="px-3 py-2 rounded-lg bg-gray-100 text-sm">JSON</button></div></div>
          <div className="space-y-3 max-h-80 overflow-y-auto">{consentLogs.map((log) => <div key={log.id} className="rounded-lg border bg-gray-50 p-3 text-sm" data-testid={`consent-log-${log.id}`}><p className="font-medium text-[#0A2A43]">{log.action}</p><p className="text-gray-500">{new Date(log.created_at).toLocaleString("de-DE")}</p><p className="text-gray-600">Essentiell: {String(log.essential)} · Statistik: {String(log.statistics)} · Marketing: {String(log.marketing)}</p></div>)}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <div className="flex items-center justify-between gap-3"><h3 className="font-semibold text-[#0A2A43]">Sicherheitslogs</h3><div className="flex gap-2"><button onClick={() => downloadFile(`${API}/security-logs/export?format=csv`, "security-logs.csv")} className="px-3 py-2 rounded-lg bg-gray-100 text-sm">CSV</button><button onClick={() => downloadFile(`${API}/security-logs/export?format=json`, "security-logs.json")} className="px-3 py-2 rounded-lg bg-gray-100 text-sm">JSON</button></div></div>
          <div className="space-y-3 max-h-80 overflow-y-auto">{securityLogs.map((log) => <div key={log.id} className="rounded-lg border bg-gray-50 p-3 text-sm" data-testid={`security-log-${log.id}`}><p className="font-medium text-[#0A2A43]">{log.event_type} · {log.status}</p><p className="text-gray-500">{new Date(log.created_at).toLocaleString("de-DE")}</p><p className="text-gray-600">{log.details}</p></div>)}</div>
        </div>
      </div>
    </div>
  );
};
